import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import axios from '/components/configs/axios'
import axiosBase from 'axios'
import { useRouter } from 'next/router'
import moment from 'moment'
import isEmpty from 'lodash-es/isEmpty'
import isEqual from 'lodash-es/isEqual'
import without from 'lodash-es/without'
import omit from 'lodash-es/omit'
import { getUserDates, saveUserDates, clearUserDates } from '/components/helpers/userContactStorage'

const SearchContext = createContext('default')

const useSearchContext = () => {
  const context = useContext(SearchContext)

  if (context === undefined) {
    throw new Error('useSearchContext was used outside of its Provider')
  }

  return context
}

function SearchContextProvider({ children }) {
  const router = useRouter()

  const initialValues = {
    startDate: null,
    endDate: null,
    bedrooms: null,
    bathrooms: null,
    priceStart: null,
    priceEnd: null,
    amenities: [],
    homeType: [],
    location: [],
    listing: '',
    sorting: 'default'
  }

  const [listing, setListing] = useState()
  const [loading, setLoading] = useState(false)
  const [reset, setReset] = useState(false)
  const [offsetable, setOffsetable] = useState(0)
  const [searchActive, setSearchActive] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [search, setSearch] = useState(initialValues)
  const [reserved, setReserved] = useState(false)
  // Initialize dates correctly for calendar page - start from current month, end 2 months ahead
  const initialStartDate = router.pathname === '/calendar'
    ? moment().utc().startOf('month')
    : moment().utc()
  const initialEndDate = router.pathname === '/calendar'
    ? moment().utc().add(2, 'months').endOf('month')
    : moment().utc()
  const [requestStartDate, setRequestStartDate] = useState(initialStartDate)
  const [requestEndDate, setRequestEndDate] = useState(initialEndDate)
  const [error, setError] = useState(null)

  // Request tracking to prevent race conditions
  const [requestId, setRequestId] = useState(0)
  const currentRequestIdRef = useRef(0)

  // AbortController Map for canceling in-flight requests
  // Maps request ID to AbortController for safer cleanup
  const abortControllersRef = useRef(new Map())

  // Track if initial load is complete
  const initialLoadComplete = useRef(false)

  // Track URL update source to prevent infinite loops
  const urlUpdateSource = useRef('external')

  // Track last processed URL to prevent duplicate fetches
  const lastProcessedUrl = useRef(null)

  // Track if dates have been loaded from sessionStorage
  const datesLoadedFromStorage = useRef(false)

  // for single page
  const [bedroomOptions, setBedroomOptions] = useState('')
  const [guests, setGuests] = useState(null)

  const isCalendarPage = router.asPath === '/calendar'
  const activeRoute =
    router.pathname === '/vacation-rentals' ||
    router.pathname === '/real-estate/search' ||
    router.pathname === '/calendar'
  const searchRoute =
    router.pathname === '/calendar'
      ? 'calendar/search'
      : router.pathname === '/vacation-rentals'
      ? 'listings/search'
      : 'real-estate/search'
  const allPropertiesRoute =
    router.pathname === '/calendar'
      ? 'calendar/blocked-dates'
      : router.pathname === '/vacation-rentals'
      ? 'listings/index'
      : 'real-estate/properties'

  // Helper function to parse and validate numeric URL parameters
  // Uses realistic property limits to prevent DOS attacks with oversized queries
  const parseNumericParam = (value, defaultValue, min = 0, max = Number.MAX_SAFE_INTEGER) => {
    if (!value) return defaultValue
    const parsed = Number(value)
    if (isNaN(parsed) || !isFinite(parsed)) return defaultValue
    return Math.max(min, Math.min(max, parsed))
  }

  // Helper function to build URL params from search state
  const buildUrlParams = useCallback((searchState = null) => {
    const stateToUse = searchState || search

    let filtered = Object.keys(initialValues)
      .filter((key) => !isEqual(stateToUse[key], initialValues[key]))
      .reduce((obj, key) => {
        obj[key] = stateToUse[key]
        return obj
      }, {})

    if (filtered.startDate && moment.isMoment(filtered.startDate)) {
      filtered.startDate = filtered.startDate.toISOString()
    }

    if (filtered.endDate && moment.isMoment(filtered.endDate)) {
      filtered.endDate = filtered.endDate.toISOString()
    }

    return filtered
  }, [search])

  // Helper function to update URL with shallow routing
  const updateUrl = useCallback((params) => {
    const filteredQuery = omit(router.query, Object.keys(initialValues))
    const newQuery = { ...filteredQuery, ...params }

    // Mark this as an internal URL update to prevent infinite loops
    urlUpdateSource.current = 'internal'

    router.push(
      {
        pathname: router.pathname,
        query: newQuery
      },
      undefined,
      { shallow: true }
    )
  }, [router])

  // Fetch all properties (no filters applied)
  const fetchAllProperties = useCallback(async (reqId) => {
    // Cancel any previous in-flight requests and clear map in one pass
    abortControllersRef.current.forEach((controller) => controller.abort())
    abortControllersRef.current.clear()

    // Create new AbortController for this request
    const controller = new AbortController()
    abortControllersRef.current.set(reqId, controller)

    setLoading(true)
    setError(null)

    // Only request fields needed for listing cards to reduce response size
    const cardFields = [
      'id',
      'resource_id',
      'nickname',
      'city',
      'propertyType',
      'accommodates',
      'bedrooms',
      'bathrooms',
      'picture',
      'cloudflare_gallery',
      'gallery',
      'propertyTitle',
      'is_exclusive',
      'startingAt',
      'min_price',
      'total',
      'welcome_description',
      'propertyDescription',
      'promotional',
      'card_label',
      'address'
    ]

    const params = {
      lang: router.locale,
      startDate:
        (isCalendarPage && requestStartDate && moment.isMoment(requestStartDate))
          ? requestStartDate.format('YYYY-MM-DD')
          : moment().utc().format('YYYY-MM-DD'),
      endDate:
        (isCalendarPage && requestEndDate && moment.isMoment(requestEndDate))
          ? requestEndDate.format('YYYY-MM-DD')
          : moment().utc().add(2, 'years').format('YYYY-MM-DD'),
      fields: router.pathname === '/vacation-rentals' ? cardFields : undefined
    }
    if (router.pathname === '/calendar') {
      delete params.lang
    }

    try {
      // Pass AbortController signal to axios
      const res = await axios.get(allPropertiesRoute, {
        params,
        signal: controller.signal
      })

      // Only update if this is still the current request
      if (reqId === currentRequestIdRef.current) {
        if (isEmpty(res.data)) {
          setListing(res.data)
        } else {
          setListing(res.data[router.locale])
        }
        setLoading(false)
        setError(null)
        // Clean up this request's controller
        abortControllersRef.current.delete(reqId)
      }
    } catch (error) {
      // Don't update state if request was cancelled
      if (axiosBase.isCancel(error)) {
        abortControllersRef.current.delete(reqId)
        return
      }

      if (reqId === currentRequestIdRef.current) {
        console.error('Fetch all properties error:', error)
        const errorMessage =
          error?.response?.status === 404
            ? 'No properties found'
            : error?.response?.status >= 500
              ? 'Server error. Please try again later'
              : 'Unable to load properties. Please check your connection'
        setListing([])
        setLoading(false)
        setError(errorMessage)
        abortControllersRef.current.delete(reqId)
      }
    }
  }, [router.locale, router.pathname, isCalendarPage, requestStartDate, requestEndDate, allPropertiesRoute])

  // Search properties with filters
  const searchProperties = useCallback(async (reqId, searchState) => {
    // Cancel any previous in-flight requests and clear map in one pass
    abortControllersRef.current.forEach((controller) => controller.abort())
    abortControllersRef.current.clear()

    // Create new AbortController for this request
    const controller = new AbortController()
    abortControllersRef.current.set(reqId, controller)

    const submittedStart = searchState.startDate && searchState.startDate.clone()
    const submittedEnd = submittedStart && searchState.endDate && searchState.endDate.clone()
    const submittedHomeType = without(searchState?.homeType, 'exclusive')

    setLoading(true)
    setError(null)

    // Build params object explicitly to ensure all values are included
    // Don't use spread operator to avoid issues with Moment objects and null handling
    const params = {
      lang: router.locale,
      listing: searchState.listing || '',
      sorting: searchState.sorting || 'default',
      bedrooms: searchState.bedrooms,
      bathrooms: searchState.bathrooms,
      priceStart: searchState.priceStart,
      priceEnd: searchState.priceEnd,
      amenities: searchState.amenities || [],
      location: searchState.location || [],
      homeType: submittedHomeType,
      exclusive: searchState?.homeType.includes('exclusive') ? true : null,
      startDate:
        isCalendarPage && requestStartDate?._d
          ? requestStartDate
          : submittedStart
          ? submittedStart?.subtract(offsetable, 'days')?.format('YYYY-MM-DD')
          : null,
      endDate:
        isCalendarPage && requestEndDate?._d
          ? requestEndDate
          : submittedEnd
          ? submittedEnd?.add(offsetable, 'days')?.format('YYYY-MM-DD')
          : null,
      // Only request fields needed for listing cards to reduce response size
      fields: [
        'id',
        'resource_id',
        'nickname',
        'city',
        'propertyType',
        'accommodates',
        'bedrooms',
        'bathrooms',
        'picture',
        'cloudflare_gallery',
        'gallery',
        'propertyTitle',
        'is_exclusive',
        'startingAt',
        'min_price',
        'total',
        'welcome_description',
        'propertyDescription',
        'promotional',
        'card_label',
        'address'
      ]
    }

    try {
      // Pass AbortController signal to axios
      const res = await axios.get(searchRoute, {
        params,
        signal: controller.signal
      })

      // Only update if this is still the current request
      if (reqId === currentRequestIdRef.current) {
        if (isEmpty(res.data)) {
          setListing(res.data)
        } else {
          setListing(res.data[router.locale])
        }
        setLoading(false)
        setError(null)
        // Clean up this request's controller
        abortControllersRef.current.delete(reqId)
      }
    } catch (error) {
      // Don't update state if request was cancelled
      if (axiosBase.isCancel(error)) {
        abortControllersRef.current.delete(reqId)
        return
      }

      if (reqId === currentRequestIdRef.current) {
        console.error('Search properties error:', error)
        const errorMessage =
          error?.response?.status === 404
            ? 'No properties match your search'
            : error?.response?.status >= 500
              ? 'Server error. Please try again later'
              : 'Unable to search properties. Please check your connection'
        setListing([])
        setLoading(false)
        setError(errorMessage)
        abortControllersRef.current.delete(reqId)
      }
    }
  }, [router.locale, isCalendarPage, requestStartDate, requestEndDate, offsetable, searchRoute])

  // Apply current filters - updates URL and fetches data
  // Accepts optional searchState to avoid React state timing issues
  const applyFilters = useCallback((searchState = null) => {
    // Use provided state or fall back to current search state
    const stateToUse = searchState || search

    // Increment request ID
    const newRequestId = requestId + 1
    setRequestId(newRequestId)
    currentRequestIdRef.current = newRequestId

    // Build and update URL params BEFORE fetching
    const params = buildUrlParams(stateToUse)
    updateUrl(params)

    // Fetch data with request tracking
    if (isEmpty(params)) {
      fetchAllProperties(newRequestId)
    } else {
      searchProperties(newRequestId, stateToUse)
    }
  }, [requestId, buildUrlParams, updateUrl, fetchAllProperties, searchProperties, search])

  // Reset all filters to initial values
  const resetFilters = useCallback(() => {
    // Increment request ID
    const newRequestId = requestId + 1
    setRequestId(newRequestId)
    currentRequestIdRef.current = newRequestId

    // Reset search state
    setSearch(initialValues)

    // Clear URL params
    updateUrl({})

    // Fetch all properties with no filters
    fetchAllProperties(newRequestId)
  }, [requestId, updateUrl, fetchAllProperties])

  // Enable the search on the search/real estate pages
  useEffect(() => {
    !activeRoute && setSearchActive(false)
  }, [activeRoute])

  // Cleanup: cancel any in-flight requests when component unmounts
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => controller.abort())
      abortControllersRef.current.clear()
    }
  }, [])

  useEffect(() => setLoaded(true), [])

  // Load dates from sessionStorage on mount (client-side only)
  // This runs before navigation effects to ensure dates are available
  useEffect(() => {
    if (typeof window === 'undefined' || datesLoadedFromStorage.current) return

    const savedDates = getUserDates()
    
    // Mark as loaded BEFORE updating state to prevent save effect from clearing
    datesLoadedFromStorage.current = true

    if (savedDates && (savedDates.startDate || savedDates.endDate)) {
      const loadedDates = {
        startDate: savedDates.startDate ? moment(savedDates.startDate).utc() : null,
        endDate: savedDates.endDate ? moment(savedDates.endDate).utc() : null
      }

      setSearch(prevSearch => ({
        ...prevSearch,
        ...loadedDates
      }))

      // Also restore offsetable if it was saved
      if (savedDates.offsetable !== undefined) {
        setOffsetable(savedDates.offsetable)
      }
    }
  }, [])

  // Save dates and offsetable to sessionStorage when they change
  useEffect(() => {
    // Only save after initial load
    if (!loaded) {
      return
    }

    // Don't clear sessionStorage on initial mount - wait until dates have been loaded from storage
    // This prevents clearing dates that were just loaded from sessionStorage
    if (!datesLoadedFromStorage.current) {
      return
    }
    
    // Don't clear during navigation - check if we're navigating to a page that should preserve dates
    // This prevents clearing dates when navigation effect hasn't finished loading them yet
    const isHomeOrVacationRentals = router.pathname === '/' || router.pathname === '/vacation-rentals'
    const isNavigatingToPreservePage = isHomeOrVacationRentals && !initialLoadComplete.current
    
    // Clear sessionStorage if all dates are cleared and offsetable is 0
    // Only clear if this is an intentional clear (not during navigation or initial mount)
    if (!search.startDate && !search.endDate && offsetable === 0) {
      if (isNavigatingToPreservePage) {
        return
      }
      clearUserDates()
    } else {
      // Save dates immediately when they change (don't wait for datesLoadedFromStorage flag)
      // This ensures dates are persisted even if navigation happens quickly
      saveUserDates({
        startDate: search.startDate,
        endDate: search.endDate,
        offsetable: offsetable
      })
    }
  }, [search.startDate, search.endDate, offsetable, loaded, router.pathname])

  // Single effect for navigation - handles initial load and browser back/forward
  useEffect(() => {
    if (!router.isReady || !activeRoute || !loaded) {
      return
    }

    // Skip if this URL change was triggered internally by applyFilters
    if (urlUpdateSource.current === 'internal') {
      urlUpdateSource.current = 'external'
      return
    }

    // Create unique key for current URL state to prevent duplicate processing
    // Include query string to properly detect changes in filters
    const currentUrlKey = `${router.pathname}|${JSON.stringify(router.query)}|${router.locale}`

    // Skip if we've already processed this exact URL
    if (lastProcessedUrl.current === currentUrlKey) {
      return
    }

    // Mark this URL as processed
    lastProcessedUrl.current = currentUrlKey

    // Parse URL params and sync state
    if (!isEmpty(router.query)) {
      const {
        startDate,
        endDate,
        bedrooms,
        bathrooms,
        priceStart,
        priceEnd,
        homeType,
        location,
        listing,
        amenities,
        sorting
      } = router.query

      // If dates are in URL, use them; otherwise preserve from sessionStorage or current state for home/vacation-rentals
      const isHomeOrVacationRentals = router.pathname === '/' || router.pathname === '/vacation-rentals'
      let preservedDates = {}
      
      if (isHomeOrVacationRentals && (!startDate || !endDate)) {
        // If dates not in URL, try to preserve from sessionStorage first, then current search state
        const savedDates = getUserDates()
        if (savedDates && (savedDates.startDate || savedDates.endDate)) {
          preservedDates = {
            startDate: savedDates.startDate ? moment(savedDates.startDate).utc() : null,
            endDate: savedDates.endDate ? moment(savedDates.endDate).utc() : null
          }
          // Also restore offsetable if it was saved
          if (savedDates.offsetable !== undefined) {
            setOffsetable(savedDates.offsetable)
          }
        } else if (search.startDate || search.endDate) {
          // Fall back to current search state if sessionStorage doesn't have dates
          preservedDates = {
            startDate: search.startDate,
            endDate: search.endDate
          }
        }
      }
      
      const parsedSearch = {
        location:
          typeof location === 'string'
            ? [location]
            : !isEmpty(location)
            ? [...location]
            : initialValues.location,
        amenities:
          typeof amenities === 'string'
            ? [amenities]
            : !isEmpty(amenities)
            ? [...amenities]
            : initialValues.amenities,
        homeType:
          typeof homeType === 'string'
            ? [homeType]
            : !isEmpty(homeType)
            ? [...homeType]
            : initialValues.homeType,
        priceStart: parseNumericParam(priceStart, initialValues.priceStart, 0, 5000000),
        priceEnd: parseNumericParam(priceEnd, initialValues.priceEnd, 0, 5000000),
        bedrooms: parseNumericParam(bedrooms, initialValues.bedrooms, 0, 20),
        bathrooms: parseNumericParam(bathrooms, initialValues.bathrooms, 0, 20),
        startDate: startDate ? moment(startDate).utc() : (preservedDates.startDate !== undefined ? preservedDates.startDate : initialValues.startDate),
        endDate: endDate ? moment(endDate).utc() : (preservedDates.endDate !== undefined ? preservedDates.endDate : initialValues.endDate),
        listing: listing ? listing : initialValues.listing,
        sorting: sorting ? sorting : initialValues.sorting
      }

      // Update state with parsed values
      setSearch(parsedSearch)

      // Fetch with parsed params
      const newRequestId = requestId + 1
      setRequestId(newRequestId)
      currentRequestIdRef.current = newRequestId
      searchProperties(newRequestId, parsedSearch)
    } else {
      // No URL params - preserve dates from sessionStorage or current search state for home/vacation-rentals pages
      // Only reset other filters, keep dates if they exist
      const isHomeOrVacationRentals = router.pathname === '/' || router.pathname === '/vacation-rentals'

      if (isHomeOrVacationRentals) {
        // Check sessionStorage first, then fall back to current search state
        const savedDates = getUserDates()

        const datesToPreserve = savedDates && (savedDates.startDate || savedDates.endDate)
          ? {
              startDate: savedDates.startDate ? moment(savedDates.startDate).utc() : null,
              endDate: savedDates.endDate ? moment(savedDates.endDate).utc() : null
            }
          : (search.startDate || search.endDate)
            ? {
                startDate: search.startDate,
                endDate: search.endDate
              }
            : {
                startDate: null,
                endDate: null
              }
        
        const preservedSearch = {
          ...initialValues,
          ...datesToPreserve
        }
        
        setSearch(preservedSearch)
        
        // Also restore offsetable if it was saved
        if (savedDates && savedDates.offsetable !== undefined) {
          setOffsetable(savedDates.offsetable)
        }
      } else {
        setSearch(initialValues)
      }

      const newRequestId = requestId + 1
      setRequestId(newRequestId)
      currentRequestIdRef.current = newRequestId
      fetchAllProperties(newRequestId)
    }

    // Mark navigation as complete AFTER setting search state
    // This ensures save effect doesn't clear dates during navigation
    initialLoadComplete.current = true
    // Note: requestId is intentionally excluded from dependencies as it's updated
    // inside this effect via currentRequestIdRef. Including it would cause infinite loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname, router.query, router.isReady, loaded, activeRoute, router.locale, fetchAllProperties, searchProperties])

  return (
    <SearchContext.Provider
      value={{
        listing,
        setListing,
        search,
        setSearch,
        loading,
        initialValues,
        reset,
        setReset,
        offsetable,
        setOffsetable,
        bedroomOptions,
        setBedroomOptions,
        guests,
        setGuests,
        searchActive,
        setSearchActive,
        setReserved,
        reserved,
        setRequestEndDate,
        setRequestStartDate,
        applyFilters,
        resetFilters,
        error
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export { useSearchContext, SearchContext, SearchContextProvider }
